import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";

type Factor = { id: string; friendly_name?: string; status: string; factor_type: string };

export function MFACard() {
  const { toast } = useToast();
  const [factors, setFactors] = useState<Factor[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pendingFactor, setPendingFactor] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState("");

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (!error) setFactors((data?.totp ?? []) as Factor[]);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const startEnroll = async () => {
    setEnrolling(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
      if (error) throw error;
      setPendingFactor({ id: data.id, qr: data.totp.uri, secret: data.totp.secret });
    } catch (e: any) {
      toast({ title: "Couldn't start MFA setup", description: e.message, variant: "destructive" });
    } finally {
      setEnrolling(false);
    }
  };

  const cancelEnroll = async () => {
    if (pendingFactor) await supabase.auth.mfa.unenroll({ factorId: pendingFactor.id });
    setPendingFactor(null);
    setCode("");
  };

  const verify = async () => {
    if (!pendingFactor || code.length !== 6) return;
    setVerifying(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: pendingFactor.id });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: pendingFactor.id,
        challengeId: challenge.id,
        code,
      });
      if (vErr) throw vErr;
      toast({ title: "Two-factor authentication enabled" });
      setPendingFactor(null);
      setCode("");
      refresh();
    } catch (e: any) {
      toast({ title: "Verification failed", description: e.message, variant: "destructive" });
    } finally {
      setVerifying(false);
    }
  };

  const remove = async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      toast({ title: "Couldn't remove factor", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Two-factor authentication disabled" });
    refresh();
  };

  const verified = factors.filter((f) => f.status === "verified");

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {verified.length > 0 ? (
            <ShieldCheck className="h-5 w-5 text-primary" />
          ) : (
            <ShieldOff className="h-5 w-5 text-muted-foreground" />
          )}
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Add an extra layer of security by requiring a code from your authenticator app when you sign in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : pendingFactor ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scan this QR code with Google Authenticator, 1Password, Authy, or a similar app, then enter the 6-digit code it shows.
            </p>
            <div className="flex justify-center p-4 bg-white rounded-lg border">
              <QRCodeSVG value={pendingFactor.qr} size={180} />
            </div>
            <div className="text-xs text-muted-foreground text-center">
              Or enter this key manually: <code className="font-mono">{pendingFactor.secret}</code>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-code">Verification code</Label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={verify} disabled={verifying || code.length !== 6}>
                {verifying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Verify & enable
              </Button>
              <Button variant="ghost" onClick={cancelEnroll}>Cancel</Button>
            </div>
          </div>
        ) : verified.length > 0 ? (
          <div className="space-y-3">
            {verified.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <div className="font-medium">Authenticator app</div>
                  <div className="text-sm text-muted-foreground">{f.friendly_name || "TOTP"} · active</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => remove(f.id)}>Remove</Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 p-4 rounded-lg border">
            <div>
              <div className="font-medium">Not enabled</div>
              <div className="text-sm text-muted-foreground">
                Recommended if you track sensitive health data.
              </div>
            </div>
            <Button onClick={startEnroll} disabled={enrolling}>
              {enrolling && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Set up
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
